import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eraser, Plus, Save, Undo2 } from 'lucide-react';
import * as api from '../api';
import { useHR } from '../../hr/HRContext';
import {
  applyFilters, can, Confirm, dmy, EditModal, ErrorBox, exportRows, Filters, invalidate, Kpi, Loading, matches, n2, naira, PeriodPick, plabel, printRows, PyHeader,
  SearchBox, Sel, sum, Toolbar, usePyData, usePyMeta, usePyPeriod, Workbook,
} from '../py';
import { Approval, Status } from '../shared';
import { DateInput, MonthInput, Select } from '../../hr/components/Controls';

// Stephanet's Monthly Inputs (custom/pay15/Inputs.jsx): Monthly Deductions, Other Expenses, Loan Schedule and Basic Savings Deduction,
// ported with the same sheets, columns and figures. Kept from this app (the user's decision): a month's sheet, a loan and a savings plan
// are approved on the approval engine before payroll uses them — so each sheet has its approval bar, and each loan / plan its approval
// panel; the engine's figures on the right count only what is approved.

function useMonth() {
  const [meta] = usePyMeta();
  const [period, setPeriod] = usePyPeriod(meta);
  const [d, err, reload] = usePyData(period);
  return { meta, period, setPeriod, d, err, reload };
}

// an editable sheet: frozen employee columns, input columns (typed), computed columns (Stephanet's InputSheet)
function InputSheet({ rows, inputs, values, draft, setDraft, computed, edit }) {
  const v = (r, k) => (draft[r.id] && k in draft[r.id] ? draft[r.id][k] : values[r.id]?.[k] ?? '');
  const set = (r, k, x) => setDraft({ ...draft, [r.id]: { ...(draft[r.id] || {}), [k]: x } });
  const tot = c => rows.reduce((t, r) => t + (Number(c.get ? c.get(r) : v(r, c.k)) || 0), 0);
  return (
    <div className="ps-wb" style={{ maxHeight: 600 }}>
      <table className="ps-table py-input">
        <thead><tr>
          <th className="fz" style={{ left: 0, minWidth: 90 }}>Employee ID</th><th className="fz" style={{ left: 90, minWidth: 170 }}>Name</th><th>Pay structure</th><th>Home site</th>
          {inputs.map(c => <th key={c.k} className="py-in-h">{c.label}</th>)}{computed.map(c => <th key={c.k} className="r">{c.label}</th>)}
        </tr></thead>
        <tbody>{rows.map(r => <tr key={r.id} className={draft[r.id] ? 'py-dirty' : ''}>
          <td className="fz" style={{ left: 0, minWidth: 90 }}>{r.employee_no}</td><td className="fz" style={{ left: 90, minWidth: 170 }}>{r.name}</td><td>{r.structure}</td><td>{r.site}</td>
          {inputs.map(c => <td key={c.k} className="py-in">{c.type === 'text' || c.type === 'date'
            ? <input type={c.type} disabled={!edit} value={v(r, c.k) ?? ''} onChange={e => set(r, c.k, e.target.value)} className={c.type === 'text' ? 'wide' : ''} />
            : <input type="number" min="0" step="any" disabled={!edit} value={v(r, c.k) === 0 ? '' : v(r, c.k)} placeholder="0" onChange={e => set(r, c.k, e.target.value === '' ? 0 : Number(e.target.value))} />}</td>)}
          {computed.map(c => <td key={c.k} className={`r mono ${c.cls ? c.cls(r) : ''}`}>{c.num ? (Number(c.get(r)) || '') : n2(c.get(r))}</td>)}
        </tr>)}</tbody>
        <tfoot><tr><td className="fz" style={{ left: 0 }} /><td className="fz" style={{ left: 90 }}>TOTAL ({rows.length})</td><td /><td />
          {inputs.map(c => <td key={c.k} className="r mono">{c.type ? '' : n2(tot(c))}</td>)}{computed.map(c => <td key={c.k} className="r mono">{c.num ? tot(c) : n2(tot(c))}</td>)}</tr></tfoot>
      </table>
    </div>
  );
}

// a month's sheet of entries (this app's PAY_INPUTS batch): its values by employee and entry type, its status and approval
function useSheet(period, name) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => (period ? api.sheet(period, name).then(x => { setS(x); setErr(''); return x; }).catch(e => setErr(e.message)) : Promise.resolve()), [period, name]);
  useEffect(() => { load(); }, [load]);
  // { employee id: { type id: amount, note, date } }
  const values = useMemo(() => {
    const out = {};
    (s?.rows || []).forEach(r => {
      const v = {};
      Object.entries(r.values).forEach(([t, x]) => { v[t] = x.amount; if (x.note && !v.note) v.note = x.note; if (x.entry_date && !v.date) v.date = x.entry_date; });
      out[r.id] = v;
    });
    return out;
  }, [s]);
  return [s, values, load, err];
}

// the draft → entries: every amount typed; a note (and a date) goes on each of the person's entries (on the first column when there is no amount)
const toEntries = (draft, values, types) => Object.entries(draft).flatMap(([emp, x]) => {
  const merged = { ...(values[emp] || {}), ...x };
  const note = merged.note || null;
  const dt = merged.date || null;
  const rows = types.map(t => ({ employee_id: emp, type_id: t.id, amount: Number(merged[t.id] || 0), note, entry_date: dt }));
  if (note && !rows.some(r => r.amount)) rows[0] = { ...rows[0], note };
  return rows.map(r => (r.amount || r === rows[0] ? r : { ...r, note: null }));
});

function SheetBar({ s, notify, onDone }) {
  if (!s?.batch_id) return <p className="muted small">Nothing entered for {s?.period_label || 'this month'} yet — the sheet goes for approval once saved and submitted.</p>;
  const act = async (a, note) => {
    try { const r = await api.sheetAction(s.batch_id, a, note); notify(r.message); onDone(); } catch (e) { notify(e.message, 'bad'); }
  };
  return (
    <div className="mdr-card" style={{ padding: '10px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Approval</b><Status s={s.status} />
        {s.locked && <span className="muted small">The payroll month is {s.period_status.replace(/_/g, ' ')} — its inputs are locked.</span>}
        {!s.editable && !s.locked && !['Draft', 'Returned'].includes(s.status) && <span className="muted small">Reopen the sheet to change it.</span>}
      </div>
      <Approval route={s.route} history={s.history} actions={s.actions} onAction={act} />
    </div>
  );
}

// ------------------------------------------------------------------ MONTHLY DEDUCTIONS

export function Deductions() {
  const { notify } = useHR();
  const { meta, period, setPeriod, d, err, reload } = useMonth();
  const [s, vals, loadSheet, sErr] = useSheet(period, 'Deductions & Adjustments');
  const [draft, setDraft] = useState({});
  const [f, setF] = useState({ q: '', structure: '', site: '' });
  const [only, setOnly] = useState('');
  const [busy, setBusy] = useState(false);
  const [clear, setClear] = useState(false);
  const rows = useMemo(() => (d ? applyFilters(d.rows, f).filter(r => !only || (only === 'paid' ? r.paid : Object.keys(vals[r.id] || {}).length)) : []), [d, f, only, vals]);
  if (err || sErr) return <ErrorBox error={err || sErr} />;
  if (!d || !s) return <Loading what="monthly deductions" />;
  const edit = can(meta, 'py-deductions') && s.editable;
  const inputs = [...s.types.map(t => ({ k: String(t.id), label: t.kind === 'DAYS' ? t.name : `${t.name} (₦)` })), { k: 'note', label: 'Note', type: 'text' }];
  const computed = [{ k: 'md', label: 'Missed days charged', get: r => r.missed_days, num: true }, { k: 'mdd', label: 'Missed-day deduction', get: r => r.missed_deduction },
    { k: 'pe', label: 'Pension (employee)', get: r => r.pension_ee }, { k: 'pa', label: 'PAYE (employee)', get: r => r.paye }, { k: 'nh', label: 'NHF', get: r => r.nhf },
    { k: 'ln', label: 'Loan repayment', get: r => r.loan }, { k: 'sv', label: 'Basic savings withheld', get: r => r.savings },
    { k: 'ap', label: 'Advance + losses + penalty + other', get: r => r.advance + r.losses + r.penalty + r.other_ded },
    { k: 'td', label: 'TOTAL DEDUCTIONS', get: r => r.total_ded }, { k: 'np', label: 'Net pay', get: r => r.net, cls: r => (r.net < 0 ? 'py-bad' : '') }];
  const n = Object.keys(draft).length;
  const after = () => { setDraft({}); invalidate(); reload(); loadSheet(); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.saveSheet(period, 'Deductions & Adjustments', toEntries(draft, vals, s.types)); notify(r.message); after(); }
    catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  return (
    <div className="ps py">
      <PyHeader title="Monthly Deductions & Adjustments" sub="This month's leave days, advances, losses, penalties, other deductions and additions. Leave / sick days on attendance and HR expenses are added automatically." meta={meta} period={period}>
        {edit && <button className="mdr-btn" onClick={() => setClear(true)}><Eraser size={14} /> Clear Month</button>}
        {edit && <button className="mdr-btn" disabled={!n} onClick={() => setDraft({})}><Undo2 size={14} /> Undo</button>}
        {edit && <button className="mds-run" disabled={busy || !n} onClick={save}><Save size={14} /> Save{n ? ` (${n})` : ''}</button>}
      </PyHeader>
      <SheetBar s={s} notify={notify} onDone={after} />
      <Filters meta={meta} period={period} setPeriod={p => { setDraft({}); setPeriod(p); }} f={f} setF={setF}>
        <Sel label="Rows" value={only} onChange={setOnly} all="Everyone" options={[{ value: 'paid', label: 'Paid this month' }, { value: 'entered', label: 'With entries' }]} />
      </Filters>
      <div className="ps-kpis five">
        <Kpi label="Missed-Day Deductions" value={naira(sum(rows, 'missed_deduction'))} tone="red" />
        <Kpi label="Advances + Losses + Penalties + Other" value={naira(sum(rows, r => r.advance + r.losses + r.penalty + r.other_ded))} tone="amber" i={1} />
        <Kpi label="Additions" value={naira(sum(rows, 'additions'))} tone="green" i={2} />
        <Kpi label="Total Deductions" value={naira(sum(rows, 'total_ded'))} tone="purple" i={3} />
        <Kpi label="Net Pay" value={naira(sum(rows, 'net'))} tone="blue" i={4} />
      </div>
      <InputSheet rows={rows} inputs={inputs} values={vals} draft={draft} setDraft={setDraft} computed={computed} edit={edit} />
      <p className="muted small py-foot">Figures on the right are the payroll's: they count the sheet once it is approved. Excused days reduce missed days (Net and Gross staff only).</p>
      {clear && <Confirm title="Clear this month" danger yes="Clear" text={`Remove every monthly deduction / adjustment entered for ${plabel(period)}?`} onClose={() => setClear(false)}
        onYes={async () => {
          const zero = Object.fromEntries(Object.keys(vals).map(emp => [emp, { ...Object.fromEntries(s.types.map(t => [String(t.id), 0])), note: '', date: '' }]));
          try { const r = await api.saveSheet(period, 'Deductions & Adjustments', toEntries(zero, {}, s.types)); notify(r.message); } catch (e) { notify(e.message, 'bad'); }
          setClear(false); after();
        }} />}
    </div>
  );
}

// ------------------------------------------------------------------ OTHER EXPENSES

export function OtherExpenses() {
  const { notify } = useHR();
  const { meta, period, setPeriod, d, err, reload } = useMonth();
  const [s, vals, loadSheet, sErr] = useSheet(period, 'Other Expenses');
  const [draft, setDraft] = useState({});
  const [f, setF] = useState({ q: '', structure: '', site: '', dept: '' });
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => (d ? applyFilters(d.rows, f) : []), [d, f]);
  if (err || sErr) return <ErrorBox error={err || sErr} />;
  if (!d || !s) return <Loading what="other expenses" />;
  const keys = s.types.map(t => String(t.id));
  const label = Object.fromEntries(s.types.map(t => [String(t.id), t.name]));
  const edit = can(meta, 'py-other-exp') && s.editable;
  const val = (r, k) => (draft[r.id] && k in draft[r.id] ? draft[r.id][k] : vals[r.id]?.[k] || 0);
  const inputs = [{ k: 'date', label: 'Date', type: 'date' }, ...keys.map(k => ({ k, label: label[k].toUpperCase() })), { k: 'note', label: 'Note', type: 'text' }];
  const computed = [{ k: 'total', label: 'TOTAL', get: r => keys.reduce((t, k) => t + (Number(val(r, k)) || 0), 0) }];
  const n = Object.keys(draft).length;
  const after = () => { setDraft({}); invalidate(); reload(); loadSheet(); };
  const save = async () => {
    setBusy(true);
    try { const r = await api.saveSheet(period, 'Other Expenses', toEntries(draft, vals, s.types)); notify(r.message); after(); }
    catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  return (
    <div className="ps py">
      <PyHeader title="Other Expenses" sub="Staff costs paid outside salary this month — they add to cost to company and Financial, not to pay." meta={meta} period={period}>
        {edit && <button className="mdr-btn" disabled={!n} onClick={() => setDraft({})}><Undo2 size={14} /> Undo</button>}
        {edit && <button className="mds-run" disabled={busy || !n} onClick={save}><Save size={14} /> Save{n ? ` (${n})` : ''}</button>}
      </PyHeader>
      <SheetBar s={s} notify={notify} onDone={after} />
      <Filters meta={meta} period={period} setPeriod={p => { setDraft({}); setPeriod(p); }} f={f} setF={setF} show={['structure', 'site', 'dept', 'q']} />
      <div className="ps-kpis five">
        {keys.slice(0, 4).map((k, i) => <Kpi key={k} label={label[k]} value={naira(sum(rows, r => val(r, k)))} i={i} />)}
        <Kpi label="Total Other Expenses" value={naira(sum(rows, r => keys.reduce((t, k) => t + (Number(val(r, k)) || 0), 0)))} tone="navy" i={4} />
      </div>
      <InputSheet rows={rows} inputs={inputs} values={vals} draft={draft} setDraft={setDraft} computed={computed} edit={edit} />
    </div>
  );
}

// a loan's or a savings plan's approval route, history and actions, inside its form
function RequestPanel({ get, act, notify, onDone, children }) {
  const [a, setA] = useState(null);
  useEffect(() => { get().then(setA).catch(e => notify(e.message, 'bad')); }, [get, notify]);
  if (!a) return <div className="muted small">Loading the approval…</div>;
  const run = async (action, note, extra = {}) => {
    try { const r = await act({ action, note, ...extra }); notify(r.message); onDone(); } catch (e) { notify(e.message, 'bad'); }
  };
  return (
    <div style={{ marginTop: 14 }}>
      <h4 className="wh-sub">Approval <Status s={a.status} /></h4>
      <Approval route={a.route} history={a.history} actions={a.actions} onAction={run} skip={['edit', 'repay', 'loss', 'refund']} />
      {children && children(a.actions, run)}
    </div>
  );
}

// ------------------------------------------------------------------ LOAN SCHEDULE

export function Loans() {
  const { notify } = useHR();
  const { meta, period, setPeriod } = useMonth();
  const [x, setX] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [st, setSt] = useState('');
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => (period ? api.loans(period).then(setX).catch(e => setErr(e.message)) : null), [period]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (x ? x.rows.map(l => ({ ...l, ref: l.reference, type: l.loan_type, granted: l.granted_on, name: l.name || 'ID NOT FOUND' }))
    .filter(l => (!st || l.status.toUpperCase() === st) && matches(l, q, ['ref', 'employee_no', 'name', 'type', 'note'])) : []), [x, q, st]);
  if (err) return <ErrorBox error={err} />;
  if (!x) return <Loading what="the loan schedule" />;
  const cols = [{ key: 'ref', label: 'Loan ref', w: 80 }, { key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Name', w: 170 }, { key: 'type', label: 'Type', w: 80 },
    { key: 'granted', label: 'Date granted', w: 100, get: r => (r.granted ? dmy(r.granted) : '') }, { key: 'principal', label: 'Principal (₦)', kind: 'money' },
    { key: 'instalment', label: 'Monthly instalment (₦)', kind: 'money' }, { key: 'first_month', label: 'First deduction month', w: 120, get: r => (r.first_month ? plabel(r.first_month.slice(0, 7)) : '') },
    { key: 'repaid_before', label: 'Repaid before first month', kind: 'money' }, { key: 'status', label: 'Status', w: 80 },
    { key: 'months_to_repay', label: 'Months to repay', kind: 'num', w: 80 }, { key: 'months_elapsed', label: 'Months elapsed', kind: 'num', w: 80 },
    { key: 'repaid_to_date', label: 'Repaid before this payroll', kind: 'money' }, { key: 'this_month', label: 'THIS MONTH DEDUCTION', kind: 'money', cls: 'py-key' },
    { key: 'balance', label: 'Balance after this month', kind: 'money' }, { key: 'note', label: 'Note', w: 180 }];
  const emp = x.employees.map(e => ({ value: String(e.id), label: e.label }));
  const editable = !edit?.id || ['Draft', 'Returned'].includes(edit.status);
  const fields = [{ k: 'ref', label: 'Loan ref', hint: 'Blank = next number', disabled: true }, { k: 'employee_id', label: 'Employee', type: 'select', req: true, options: emp, disabled: !editable },
    { k: 'type', label: 'Type', type: 'select', options: x.types, blank: false, disabled: !editable },
    { k: 'granted', label: 'Date granted', type: 'date', disabled: !editable }, { k: 'principal', label: 'Principal (₦)', type: 'money', req: true, disabled: !editable,
      hint: `Above ₦${n2(x.threshold)} the MD approves too (Setup Rules)` },
    { k: 'instalment', label: 'Monthly instalment (₦)', type: 'money', req: true, disabled: !editable }, { k: 'first_month', label: 'First deduction month', type: 'month', req: true, disabled: !editable },
    { k: 'repaid_before', label: 'Repaid before first month (₦)', type: 'money', disabled: !editable }, { k: 'note', label: 'Note', type: 'textarea' }];
  const done = async r => { if (r?.message) notify(r.message); setEdit(null); invalidate(); load(); };
  const body = v => ({ employee_id: v.employee_id, loan_type: v.type, granted_on: v.granted || null, principal: v.principal, instalment: v.instalment,
    first_month: v.first_month, repaid_before: v.repaid_before || 0, note: v.note });
  return (
    <div className="ps py">
      <PyHeader title="Loan Schedule" sub="Enter the principal, monthly instalment and first deduction month once — this month's instalment and the balance update for any payroll month." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows(`loan-schedule-${period}.csv`, cols, rows)} onPrint={() => printRows(`Loan Schedule — ${plabel(period)}`, `${rows.length} loan(s)`, cols, rows)}>
          {can(meta, 'py-loans', 'create') && <button className="mds-run" onClick={() => setEdit({ type: 'LOAN', first_month: period })}><Plus size={14} /> Add Loan</button>}
        </Toolbar>
      </PyHeader>
      <div className="mdr-card ps-filters no-print">
        <PeriodPick meta={meta} value={period} onChange={setPeriod} />
        <Sel label="Status" value={st} onChange={setSt} options={['DRAFT', 'SUBMITTED', 'RETURNED', 'ACTIVE', 'PAUSED', 'CLOSED']} /><SearchBox value={q} onChange={setQ} placeholder="Search ref, employee, note…" />
      </div>
      <div className="ps-kpis four">
        <Kpi label="Loans" value={rows.length} note={`${rows.filter(r => r.status === 'Active').length} active`} />
        <Kpi label="Principal" value={naira(sum(rows, 'principal'))} tone="indigo" i={1} />
        <Kpi label="This Month's Deductions" value={naira(sum(rows, 'this_month'))} tone="green" i={2} />
        <Kpi label="Balance After This Month" value={naira(sum(rows, r => r.balance || 0))} tone="amber" i={3} />
      </div>
      <Workbook cols={cols} rows={rows} frozen={3} maxHeight={600} rowClass={r => (r.name === 'ID NOT FOUND' ? 'py-row-bad' : '')}
        onRow={rows.length ? r => setEdit({ ...r, employee_id: String(r.employee_id), first_month: r.first_month?.slice(0, 7) }) : undefined} empty="No loans." />
      <p className="muted small py-foot">A new loan is approved (HR Review → Finance Approval, and the MD above the Setup threshold) before it is deducted. Only ACTIVE loans of staff paid this month are deducted.</p>
      {edit && <EditModal title={edit.id ? `Loan ${edit.ref}` : 'Add Loan'} value={edit} fields={fields} onClose={() => setEdit(null)}
        saveLabel={editable ? 'Save draft' : 'Close'}
        onSave={async v => (editable ? done(await api.saveLoan(v.id, body(v))) : setEdit(null))}>
        {v => v.id ? <RequestPanel get={() => api.loanApproval(v.id)} act={b => api.loanAction(v.id, b)} notify={notify} onDone={() => done()}>
          {(actions, run) => actions.includes('repay') && <CashRepayment period={period} onSave={b => run('repay', b.note, b)} />}
        </RequestPanel> : null}
      </EditModal>}
    </div>
  );
}

function CashRepayment({ period, onSave }) {
  const [r, setR] = useState({ amount: '', method: 'Cash', period, paid_on: '' });
  return (
    <div className="rec-form-grid" style={{ marginTop: 10 }}>
      <label className="rec-f"><span>Cash / transfer repayment (₦)</span><input type="number" value={r.amount} onChange={e => setR({ ...r, amount: e.target.value })} /></label>
      <label className="rec-f"><span>Method</span><Select title="Method" value={r.method} onChange={e => setR({ ...r, method: e.target.value })}><option>Cash</option><option>Transfer</option></Select></label>
      <label className="rec-f"><span>Month</span><MonthInput value={r.period} onChange={e => setR({ ...r, period: e.target.value })} /></label>
      <label className="rec-f"><span>Paid on</span><DateInput value={r.paid_on} onChange={e => setR({ ...r, paid_on: e.target.value })} /></label>
      <button className="mdr-btn" disabled={!r.amount} onClick={() => onSave(r)}>Record repayment</button>
    </div>
  );
}

// ------------------------------------------------------------------ BASIC SAVINGS DEDUCTION

export function Savings() {
  const { notify } = useHR();
  const { meta, period, setPeriod } = useMonth();
  const [x, setX] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => (period ? api.savings(period).then(setX).catch(e => setErr(e.message)) : null), [period]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (x ? x.rows.map(s => ({ ...s, start: s.start_month, name: s.name || 'ID NOT FOUND' })).filter(s => matches(s, q, ['employee_no', 'name', 'note'])) : []), [x, q]);
  if (err) return <ErrorBox error={err} />;
  if (!x) return <Loading what="basic savings" />;
  const pc = v => `${+(Number(v || 0) * 100).toFixed(2)}%`;
  const cols = [{ key: 'employee_no', label: 'Employee ID', w: 96 }, { key: 'name', label: 'Name', w: 170 }, { key: 'applies', label: 'Applies?', w: 70, get: r => (r.applies ? 'Y' : 'N') },
    { key: 'withhold_pct', label: 'Withhold each month (% of Basic)', w: 110, get: r => pc(r.withhold_pct) }, { key: 'target_pct', label: 'Target to hold (% of Basic)', w: 110, get: r => pc(r.target_pct) },
    { key: 'start', label: 'Start month', w: 100, get: r => (r.start ? plabel(r.start.slice(0, 7)) : '') }, { key: 'withheld_before', label: 'Withheld before start', kind: 'money' },
    { key: 'losses', label: 'Losses / charges to date', kind: 'money' }, { key: 'refunded', label: 'Refunded / released to date', kind: 'money' },
    { key: 'monthly_basic', label: 'Monthly Basic', kind: 'money' }, { key: 'target', label: 'Target amount', kind: 'money' }, { key: 'monthly', label: 'Monthly amount', kind: 'money' },
    { key: 'months_elapsed', label: 'Months elapsed', kind: 'num', w: 80 }, { key: 'withheld_before_month', label: 'Withheld up to last month', kind: 'money' },
    { key: 'this_month', label: 'THIS MONTH WITHHELD', kind: 'money', cls: 'py-key' }, { key: 'to_date', label: 'Total withheld to date', kind: 'money' },
    { key: 'balance', label: 'BALANCE HELD', kind: 'money', cls: 'py-key' }, { key: 'status', label: 'Approval', w: 90 }, { key: 'note', label: 'Note', w: 160 }];
  const emp = x.employees.map(e => ({ value: String(e.id), label: e.label }));
  const editable = !edit?.id || ['Draft', 'Returned'].includes(edit.status);
  const fields = [{ k: 'employee_id', label: 'Employee', type: 'select', req: true, options: emp, disabled: !!edit?.id }, { k: 'applies', label: 'Applies', type: 'check', disabled: !editable },
    { k: 'withhold_pct', label: 'Withhold each month (% of Basic)', type: 'pct', disabled: !editable }, { k: 'target_pct', label: 'Target to hold (% of Basic)', type: 'pct', disabled: !editable },
    { k: 'start', label: 'Start month', type: 'month', disabled: !editable }, { k: 'withheld_before', label: 'Withheld before start (₦)', type: 'money', disabled: !editable },
    { k: 'note', label: 'Note', type: 'textarea' }];
  const done = async r => { if (r?.message) notify(r.message); setEdit(null); invalidate(); load(); };
  const body = v => ({ employee_id: v.employee_id, applies: v.applies !== false, withhold_pct: v.withhold_pct, target_pct: v.target_pct, start_month: v.start,
    withheld_before: v.withheld_before || 0, note: v.note });
  return (
    <div className="ps py">
      <PyHeader title="Basic Savings Deduction" sub="A % of Basic is withheld each month until the target is held; losses are charged against it and refunds recorded when released." meta={meta} period={period}>
        <Toolbar onExport={() => exportRows(`basic-savings-${period}.csv`, cols, rows)} onPrint={() => printRows(`Basic Savings — ${plabel(period)}`, `${rows.length} line(s)`, cols, rows)}>
          {can(meta, 'py-savings', 'create') && <button className="mds-run" onClick={() => setEdit({ applies: true, withhold_pct: 0.1, target_pct: 0.5, start: period })}><Plus size={14} /> Add Line</button>}
        </Toolbar>
      </PyHeader>
      <div className="mdr-card ps-filters no-print"><PeriodPick meta={meta} value={period} onChange={setPeriod} /><SearchBox value={q} onChange={setQ} /></div>
      <div className="ps-kpis four">
        <Kpi label="Staff in the Scheme" value={rows.filter(r => r.applies).length} />
        <Kpi label="Withheld This Month" value={naira(sum(rows, 'this_month'))} tone="green" i={1} />
        <Kpi label="Total Withheld to Date" value={naira(sum(rows, 'to_date'))} tone="indigo" i={2} />
        <Kpi label="Balance Held by Company" value={naira(sum(rows, 'balance'))} tone="amber" i={3} />
      </div>
      <Workbook cols={cols} rows={rows} frozen={2} maxHeight={600} onRow={r => setEdit({ ...r, employee_id: String(r.employee_id), start: r.start?.slice(0, 7) })} empty="No savings lines." />
      <p className="muted small py-foot">A new line (or a change to its rates) is approved (HR Approval) before anything is withheld. Losses and refunds are recorded on an approved line.</p>
      {edit && <EditModal title={edit.id ? `Savings — ${edit.name}` : 'Add Savings Line'} value={edit} fields={fields} onClose={() => setEdit(null)}
        saveLabel={editable ? 'Save draft' : 'Close'}
        onSave={async v => (editable ? done(await api.savePlan(v.id, body(v))) : setEdit(null))}>
        {v => v.id ? <RequestPanel get={() => api.planApproval(v.id)} act={b => api.planAction(v.id, b)} notify={notify} onDone={() => done()}>
          {(actions, run) => (actions.includes('loss') || actions.includes('refund')) && <Movement period={period} onSave={(kind, b) => run(kind, b.note, b)} />}
        </RequestPanel> : null}
      </EditModal>}
    </div>
  );
}

function Movement({ period, onSave }) {
  const [m, setM] = useState({ amount: '', period, note: '' });
  return (
    <div className="rec-form-grid" style={{ marginTop: 10 }}>
      <label className="rec-f"><span>Loss / charge or refund (₦)</span><input type="number" value={m.amount} onChange={e => setM({ ...m, amount: e.target.value })} /></label>
      <label className="rec-f"><span>Month</span><MonthInput value={m.period} onChange={e => setM({ ...m, period: e.target.value })} /></label>
      <label className="rec-f s2"><span>Note</span><input value={m.note} onChange={e => setM({ ...m, note: e.target.value })} /></label>
      <button className="mdr-btn" disabled={!m.amount} onClick={() => onSave('loss', m)}>Charge loss</button>
      <button className="mdr-btn" disabled={!m.amount} onClick={() => onSave('refund', m)}>Record refund</button>
    </div>
  );
}
